import { useSyncExternalStore } from 'react'

export type Theme = 'dark' | 'light' | 'system'

const STORAGE_KEY = 'vharta-theme'
const DEFAULT_THEME: Theme = 'dark'

interface ThemeSnapshot {
  theme: Theme
  resolvedTheme: 'dark' | 'light'
}

function getSystemTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: Theme): 'dark' | 'light' {
  return theme === 'system' ? getSystemTheme() : theme
}

// Validate rather than blind-cast values coming from localStorage (initial read
// AND cross-tab storage events). Same-origin localStorage tampering isn't a
// privilege boundary, but an arbitrary string flows into classList.add, which
// throws a DOMException on whitespace/invalid tokens — so a junk value would
// crash the theme apply. Falling back to the default keeps that inert. (The
// pre-rewrite hook cast without checking; this closes that robustness nit.)
function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light' || value === 'system'
}

function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const stored = localStorage.getItem(STORAGE_KEY)
  return isTheme(stored) ? stored : DEFAULT_THEME
}

// Applies the resolved theme to the document. The ONLY place that touches
// classList/meta — called exclusively by the store (constructor + refresh),
// never per-hook-instance, so N mounted consumers never race to write the
// same DOM node.
// Window (ms) during which `.theme-transition` is present on <html> on the
// FALLBACK path, letting globals.css cross-fade colors instead of snapping.
// Must match the transition duration declared there.
const THEME_TRANSITION_MS = 220
let themeTransitionTimer: ReturnType<typeof setTimeout> | undefined

// Not yet in TypeScript's DOM lib. Feature-detected at the call site, never
// assumed — Firefox has no implementation and takes the fallback path.
type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => { finished: Promise<void> }
}

// The actual DOM mutation. Pulled out so it can run either directly, or inside
// a View Transition callback where the browser captures a before/after snapshot
// around it.
function commitThemeClass(resolved: 'dark' | 'light') {
  const root = document.documentElement
  root.classList.remove('dark', 'light')
  root.classList.add(resolved)

  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#09090b' : '#ffffff')
  }
}

// `onCommit` runs immediately after the class actually lands on <html>, never
// before. This ordering is load-bearing: several canvases (FlowCanvasShell,
// ChainBuilder, GraphCanvas, ExecutionFlowCanvas) derive their OWN local
// .dark/.light class and React Flow `colorMode` from the store snapshot rather
// than inheriting from <html>, because --canvas-* tokens are theme-class
// scoped and those trees are portalled. The old implementation swapped the
// class synchronously, so notifying listeners straight afterwards was safe.
// startViewTransition defers the swap into a browser-scheduled callback — so
// notifying on return would re-render those canvases into the NEW theme while
// <html>, and therefore all the surrounding chrome, was still on the OLD one.
function applyThemeClass(resolved: 'dark' | 'light', onCommit?: () => void) {
  if (typeof document === 'undefined') {
    // No DOM to write, but subscribers must still be told the snapshot moved —
    // refresh() used to notify them separately and this branch must not
    // silently swallow that.
    onCommit?.()
    return
  }

  const root = document.documentElement
  const commit = () => {
    commitThemeClass(resolved)
    onCommit?.()
  }

  // Animate only when the theme actually CHANGES (never on first paint — that
  // would fade the whole app in on load) and never for reduced-motion users.
  const isChange = !root.classList.contains(resolved)
  const isFirstPaint = !root.classList.contains('dark') && !root.classList.contains('light')
  const allowMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches

  if (!isChange || isFirstPaint || !allowMotion) {
    commit()
    return
  }

  // PRIMARY: View Transitions. The browser cross-fades two composited snapshots
  // on the GPU, so the cost does not scale with DOM size. The old approach —
  // `html.theme-transition *` with !important — made the engine build
  // 6 properties × 3 targets × N elements of transition state on each of three
  // full-document recalcs per toggle: measured at 2,863ms of blocking work on a
  // 5,000-node screen, ~89% of it interpolation setup for elements whose
  // colours never changed. See the matching comment in globals.css.
  const startViewTransition = (document as DocumentWithViewTransition).startViewTransition
  if (typeof startViewTransition === 'function') {
    try {
      // .finished rejects if a second toggle interrupts this one — expected,
      // and an unhandled rejection would surface as a console error. The
      // update callback still runs when a transition is skipped or aborted, so
      // the class always lands; only the cross-fade is cut short. commit()
      // writes an absolute end state, never a relative toggle, so whichever
      // callback runs last is still correct.
      startViewTransition.call(document, commit).finished.catch(() => {})
      return
    } catch {
      // Never let a decorative animation cost us the theme swap itself.
      commit()
      return
    }
  }

  // FALLBACK (no View Transitions — Firefox today): the class-based fade, now
  // pared to three properties on real elements only. Measured: that trim alone
  // is within noise — the cost is matching `*`, not the property list — so this
  // path performs about as it did before. Kept because it is strictly cheaper
  // and because a fade beats a hard snap; the actual fix is the path above.
  root.classList.add('theme-transition')
  clearTimeout(themeTransitionTimer)
  themeTransitionTimer = setTimeout(
    () => root.classList.remove('theme-transition'),
    THEME_TRANSITION_MS,
  )
  commit()
}

// Fixed, referentially-stable snapshot for useSyncExternalStore's server
// branch. Never recomputed — SSR always renders the default theme; the
// inline FOUC <script> in index.html reconciles the class before paint.
const SERVER_SNAPSHOT: ThemeSnapshot = { theme: DEFAULT_THEME, resolvedTheme: 'dark' }

/**
 * Module-singleton theme store, mirroring the WebSocketManager pattern in
 * useWebSocket.ts. Constructed once at module import; every useTheme()
 * instance subscribes to the SAME store via useSyncExternalStore, so a
 * setTheme() call from one mounted consumer (e.g. the header toggle) is
 * immediately visible to every other mounted consumer (chain canvas,
 * workflow canvas, sonner Toaster, command palette) without a remount.
 *
 * getSnapshot() MUST return a referentially-stable object between real
 * changes — allocating a fresh object on every call is the #1 cause of
 * useSyncExternalStore infinite-loop warnings, so `snapshot` is cached and
 * only reassigned inside refresh().
 */
class ThemeStore {
  private theme: Theme
  private snapshot: ThemeSnapshot
  private listeners = new Set<() => void>()

  constructor() {
    this.theme = readStoredTheme()
    this.snapshot = { theme: this.theme, resolvedTheme: resolveTheme(this.theme) }
    applyThemeClass(this.snapshot.resolvedTheme)
    this.attachListeners()
  }

  private attachListeners() {
    if (typeof window === 'undefined') return

    // Cross-tab sync only: the 'storage' event fires in OTHER tabs/windows
    // when localStorage changes, never in the tab that made the change. Same-tab
    // sync across mounted instances is handled by refresh()'s listener.forEach
    // below, not by this handler.
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return
      const next = isTheme(event.newValue) ? event.newValue : DEFAULT_THEME
      if (next === this.theme) return
      this.theme = next
      this.refresh()
    })

    // React to OS-level light/dark switches while in 'system' mode.
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    mq.addEventListener('change', () => {
      if (this.theme !== 'system') return
      this.refresh()
    })
  }

  private refresh() {
    const resolvedTheme = resolveTheme(this.theme)
    this.snapshot = { theme: this.theme, resolvedTheme }
    // Listeners are notified BY applyThemeClass, the moment the class lands on
    // <html> — not on return. On the View Transitions path the DOM write is
    // deferred, so notifying here would flip the theme-derived canvases a frame
    // ahead of the rest of the chrome. See the note above applyThemeClass.
    applyThemeClass(resolvedTheme, () => {
      this.listeners.forEach((listener) => listener())
    })
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): ThemeSnapshot => this.snapshot

  getServerSnapshot = (): ThemeSnapshot => SERVER_SNAPSHOT

  setTheme = (next: Theme) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, next)
    }
    this.theme = next
    this.refresh()
  }
}

// Single global instance — constructed once at module import.
const themeStore = new ThemeStore()

/**
 * Manages the dark/light/system theme preference.
 * Persists to localStorage and applies the .dark/.light class on <html>.
 * Backed by a module-singleton store (see ThemeStore) so every mounted
 * instance stays in sync with every other, cross-component and cross-tab.
 */
export function useTheme() {
  const snapshot = useSyncExternalStore(
    themeStore.subscribe,
    themeStore.getSnapshot,
    themeStore.getServerSnapshot,
  )

  return {
    theme: snapshot.theme,
    setTheme: themeStore.setTheme,
    resolvedTheme: snapshot.resolvedTheme,
    isDark: snapshot.resolvedTheme === 'dark',
  }
}
